import { useState, useRef } from 'react';
import { cmApi } from '../../api.js';
import { useClient } from '../../ClientContext.jsx';
import ColumnMappingGrid from './ColumnMappingGrid.jsx';
import FileInsights from './FileInsights.jsx';

const CURRENCIES = ['AED', 'SAR', 'USD', 'EUR', 'GBP'];

const toMonthDate = (month) => (month ? `${month}-01` : '');

// sourceType: 'statex' | 'paid_media' | 'offline' | 'direct_buys'
// fixedPeriod: optional 'YYYY-MM' — when set, the period is locked to this month (no picker)
// compact: when true, renders without the outer card/title, for embedding inline (e.g. a table row)
// onImported: optional callback fired after a successful commit, so a parent list can refresh
export default function UploadWizard({ sourceType, title, accept = '.xlsx,.csv', fixedPeriod, compact = false, onImported }) {
  const { clientId } = useClient();
  const fileRef = useRef(null);

  const [file, setFile] = useState(null);
  const [period, setPeriod] = useState(fixedPeriod || '');
  const [currency, setCurrency] = useState('AED');

  // step: idle | uploading | committing | mapping_required | duplicate | done | error
  const [step, setStep] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [batchId, setBatchId] = useState(null);
  const [mappingPreview, setMappingPreview] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [duplicateBatch, setDuplicateBatch] = useState(null);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const reset = () => {
    setFile(null); setPeriod(fixedPeriod || ''); setStep('idle'); setProgress(0);
    setBatchId(null); setMappingPreview(null); setHeaders([]);
    setDuplicateBatch(null); setResult(null); setErr(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const startUpload = async () => {
    if (!file || !period || !clientId) return;
    setErr(null);
    setStep('uploading');
    try {
      const { batch_id, upload_url } = await cmApi.getUploadUrl(sourceType, {
        client_id: clientId, file_name: file.name, period_month: toMonthDate(period), currency,
      });
      setBatchId(batch_id);
      await cmApi.uploadFileToBlob(upload_url, file, setProgress);
      setStep('committing');
      await runCommit(batch_id, {});
    } catch (e) {
      setErr(e.message);
      setStep('error');
    }
  };

  const runCommit = async (bId, extra) => {
    setStep('committing');
    try {
      const res = await cmApi.commitImport(sourceType, { batch_id: bId, ...extra });
      if (res.status === 'mapping_required') {
        setMappingPreview(res.mapping_preview);
        setHeaders(res.headers);
        setStep('mapping_required');
      } else if (res.status === 'duplicate') {
        setDuplicateBatch(res.existing_batch);
        setStep('duplicate');
      } else if (res.status === 'committed') {
        setResult(res);
        setStep('done');
        onImported?.(res);
      }
    } catch (e) {
      setErr(e.message);
      setStep('error');
    }
  };

  const confirmMapping = (manualMapping, saveAsDefault) => {
    runCommit(batchId, { mapping: manualMapping, save_as_default: saveAsDefault });
  };

  const confirmReplace = () => {
    runCommit(batchId, { replace_batch_id: duplicateBatch.id });
  };

  if (!clientId) {
    return <p style={{ fontSize: 12, color: '#8C94A0' }}>Select a client from the header switcher to import data.</p>;
  }

  const body = (
    <>
      {step === 'idle' && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
            {!fixedPeriod && (
              <label style={{ fontSize: 12, color: '#8C94A0' }}>
                Period (month)
                <input type="month" value={period} onChange={e => setPeriod(e.target.value)}
                  style={{ display: 'block', marginTop: 4, background: '#F5F5F2', color: '#171A1F', border: '1px solid #E3E2DD', borderRadius: 8, padding: '6px 8px', fontSize: 12 }} />
              </label>
            )}
            <label style={{ fontSize: 12, color: '#8C94A0' }}>
              Currency
              <select value={currency} onChange={e => setCurrency(e.target.value)}
                style={{ display: 'block', marginTop: 4, background: '#F5F5F2', color: '#171A1F', border: '1px solid #E3E2DD', borderRadius: 8, padding: '6px 8px', fontSize: 12 }}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <input ref={fileRef} type="file" accept={accept} onChange={e => setFile(e.target.files[0] || null)}
              style={{ fontSize: 12, color: '#171A1F' }} />
          </div>
          <button onClick={startUpload} disabled={!file || !period} style={{
            background: '#171A1F', color: '#F5F5F2', border: 'none', borderRadius: 8,
            padding: '7px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
            opacity: (!file || !period) ? 0.5 : 1,
          }}>Upload</button>
        </>
      )}

      {step === 'uploading' && (
        <div>
          <div style={{ fontSize: 12, color: '#8C94A0', marginBottom: 8 }}>Uploading… {Math.round(progress * 100)}%</div>
          <div style={{ height: 6, background: '#F5F5F2', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress * 100}%`, background: '#171A1F', transition: 'width 0.15s' }} />
          </div>
        </div>
      )}

      {step === 'committing' && <div style={{ fontSize: 12, color: '#8C94A0' }}>Processing file…</div>}

      {step === 'mapping_required' && mappingPreview && (
        <ColumnMappingGrid mappingPreview={mappingPreview} headers={headers} onConfirm={confirmMapping} onCancel={reset} submitting={false} />
      )}

      {step === 'duplicate' && duplicateBatch && (
        <div style={{ fontSize: 12 }}>
          <div style={{ color: '#A4560E', marginBottom: 8 }}>
            A file for this period was already imported on {new Date(duplicateBatch.committed_at).toLocaleString()}
            {duplicateBatch.uploaded_by ? ` by ${duplicateBatch.uploaded_by}` : ''} ({duplicateBatch.file_name}).
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={confirmReplace} style={{ background: '#171A1F', color: '#F5F5F2', border: 'none', borderRadius: 8, padding: '7px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Replace existing data
            </button>
            <button onClick={reset} style={{ background: 'transparent', color: '#8C94A0', border: '1px solid #E3E2DD', borderRadius: 8, padding: '7px 18px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === 'done' && result && (
        <div>
          <div style={{ color: '#2E6B4F', fontSize: 12, marginBottom: 12 }}>Import complete.</div>
          <FileInsights insights={result.insights} newBrands={result.new_brands} newChannels={result.new_channels} />
          <button onClick={reset} style={{ marginTop: 14, background: '#171A1F', color: '#F5F5F2', border: 'none', borderRadius: 8, padding: '7px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            Import another file
          </button>
        </div>
      )}

      {step === 'error' && (
        <div>
          <div style={{ color: '#A32B2B', fontSize: 12, marginBottom: 12 }}>{err}</div>
          <button onClick={reset} style={{ background: 'transparent', color: '#8C94A0', border: '1px solid #E3E2DD', borderRadius: 8, padding: '7px 18px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            Try again
          </button>
        </div>
      )}
    </>
  );

  if (compact) return <div>{body}</div>;

  return (
    <div style={{ background: '#FFFFFF', border: '1px solid #E3E2DD', borderRadius: 8, padding: 20, maxWidth: 560 }}>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>{title}</div>
      {body}
    </div>
  );
}
